import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import FloodNotice from "./FloodNotice";
import { useSession } from "./Account";
import SavedFinancials from "./SavedFinancials";
import FinancialComparison from "./FinancialComparison";
import EvidenceReading from "./EvidenceReading";
import { LocalityRecords } from "./LocalityPanel";
export function SaveReport({
  inputs,
  evidenceId,
  explanationId,
  finances,
  locality,
}) {
  const { user } = useSession();
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [includeFinances, setIncludeFinances] = useState(false);
  const [includeLocality, setIncludeLocality] = useState(false);
  useEffect(() => {
    setIncludeLocality(false);
    setMessage("");
  }, [locality]);
  useEffect(() => {
    setMessage("");
    setIncludeFinances(false);
  }, [finances]);
  if (!user)
    return (
      <p>
        <Link to="/account">Sign in to save and compare reports</Link>. Your
        preview is not saved yet.
      </p>
    );
  async function save() {
    setBusy(true);
    try {
      await api.post("/research/reports", {
        expected_evidence_id: evidenceId,
        ...(includeLocality && locality
          ? { expected_locality_id: locality.locality_id }
          : {}),
        ...(explanationId ? { expected_explanation_id: explanationId } : {}),
        ...(includeFinances && finances ? { finances } : {}),
        inputs: {
          ...inputs,
          price: Number(inputs.price),
          postcode: inputs.postcode || null,
          bedrooms: inputs.bedrooms ? Number(inputs.bedrooms) : null,
        },
      });
      setMessage("Report saved.");
    } catch (e) {
      setMessage(
        e.response?.status === 409
          ? "Evidence, locality or calculations changed. Refresh the report, reload locality and recalculate your scenarios before saving."
          : "Could not save this report. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div>
      {locality && (
        <label className="checkbox">
          <input
            type="checkbox"
            checked={includeLocality}
            disabled={busy}
            onChange={(e) => setIncludeLocality(e.target.checked)}
          />
          Save loaded locality records and their source dates with this report.
        </label>
      )}
      {finances && (
        <label className="checkbox">
          <input
            type="checkbox"
            checked={includeFinances}
            disabled={busy}
            onChange={(event) => setIncludeFinances(event.target.checked)}
          />
          Save completed financial scenarios and their assumptions with this
          report. They will be visible in your account and are not shared with
          partners.
        </label>
      )}
      <button disabled={busy} onClick={save}>
        {busy ? "Saving…" : "Save this report"}
      </button>
      <p role="status">
        {message}{" "}
        {message === "Report saved." && (
          <Link to="/saved">View saved reports</Link>
        )}
      </p>
    </div>
  );
}
export default function SavedReports() {
  const { user, loading, setDraft, setFinancialDraft } = useSession();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [retry, setRetry] = useState(0);
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setReports([]);
    setSelected([]);
    setError("");
    setFetching(Boolean(user));
    if (user)
      api
        .get("/research/reports")
        .then((r) => {
          if (active) setReports(r.data);
        })
        .catch(() => {
          if (active)
            setError("Saved reports are unavailable. Please try again later.");
        })
        .finally(() => {
          if (active) setFetching(false);
        });
    else {
      setReports([]);
      setSelected([]);
    }
    return () => {
      active = false;
    };
  }, [user, retry]);
  async function remove(id) {
    try {
      await api.delete(`/research/reports/${id}`);
      setReports(reports.filter((r) => r.id !== id));
      setSelected(selected.filter((value) => value !== id));
    } catch (e) {
      setError("The report could not be deleted.");
    }
  }
  if (loading) return <p>Checking your session…</p>;
  if (!user)
    return (
      <section className="research-card">
        <h1>Saved research</h1>
        <Link to="/account">Sign in to view your reports</Link>
      </section>
    );
  const compared = reports.filter((r) => selected.includes(r.id));
  return (
    <>
      <h1>Saved research</h1>
      <p>
        Select up to three reports to compare. Saved reports preserve the
        evidence available when they were created; they are not live updates.
      </p>
      {error && (
        <div className="error-state">
          <p role="alert">{error}</p>
          <button type="button" onClick={() => setRetry((value) => value + 1)}>
            Reload saved reports
          </button>
        </div>
      )}
      {fetching && <p role="status">Loading your saved research…</p>}
      {!fetching && !reports.length && !error && (
        <p>
          No saved reports yet. <Link to="/research">Research a property</Link>.
        </p>
      )}
      {reports.map((r) => (
        <article className="research-card" key={r.id}>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={selected.includes(r.id)}
              disabled={!selected.includes(r.id) && selected.length >= 3}
              onChange={(e) =>
                setSelected(
                  e.target.checked
                    ? [...selected, r.id]
                    : selected.filter((id) => id !== r.id),
                )
              }
            />
            {r.inputs.address}
          </label>
          <p className="source">
            Saved {new Date(r.created_at).toLocaleDateString("en-AU")} ·{" "}
            {r.report.evidence.schema_version}
          </p>
          <SavedFinancials finances={r.finances} />
          {r.locality && (
            <details>
              <summary>View saved locality snapshot</summary>
              <LocalityRecords locality={r.locality} saved />
            </details>
          )}
          {r.report.explanation && (
            <details>
              <summary>View saved evidence reading</summary>
              <EvidenceReading report={r.report} />
            </details>
          )}
          {r.finances && (
            <button
              className="secondary"
              onClick={() => {
                setDraft({ ...r.inputs, confirmed: false });
                setFinancialDraft({
                  owner_id: user.id,
                  address: r.inputs.address,
                  price: r.inputs.price,
                  property_type: r.inputs.property_type,
                  purchase: r.finances.purchase?.inputs || null,
                  cashflow: r.finances.cashflow?.inputs || null,
                });
                navigate("/research");
              }}
            >
              Revise financial scenarios
            </button>
          )}
          <button className="secondary" onClick={() => remove(r.id)}>
            Delete saved report
          </button>
        </article>
      ))}
      {compared.length > 0 && (
        <section className="research-card">
          <h2>Compare selected reports</h2>
          <FinancialComparison reports={compared} />
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Evidence</th>
                  {compared.map((r) => (
                    <th key={r.id}>{r.inputs.address}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {compared[0].report.evidence.metrics.map((m) => (
                  <tr key={m.id}>
                    <th>{m.label}</th>
                    {compared.map((r) => {
                      const metric = r.report.evidence.metrics.find(
                        (item) => item.id === m.id,
                      );
                      return (
                        <td key={r.id}>
                          {!metric || metric.status === "unavailable"
                            ? "Not assessed"
                            : ["AUD", "AUD/week"].includes(metric.unit)
                              ? new Intl.NumberFormat("en-AU", {
                                  style: "currency",
                                  currency: "AUD",
                                }).format(metric.value) +
                                (metric.unit === "AUD/week" ? " / week" : "")
                              : metric.value}
                          {metric?.id === "flood" &&
                            metric.status !== "unavailable" && (
                              <>
                                <p className="source">
                                  {metric.status === "stale"
                                    ? "Cached, older evidence · "
                                    : ""}
                                  {metric.source_name}
                                </p>
                                <FloodNotice />
                                {metric.source_url && (
                                  <a href={metric.source_url}>View source</a>
                                )}
                              </>
                            )}
                          <small>
                            {metric?.status === "user_provided"
                              ? "Your input"
                              : metric?.observation_period || ""}
                          </small>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
